/**
 * REST route table (all under the same origin as the SPA). Mutations need the session cookie and
 * an `x-csrf-token` header equal to SessionInfo.csrfToken. Responses are JSON; errors use
 * `{ error: ApiError }`. Query/body schemas live in ./rest.ts.
 */
const enc = encodeURIComponent;

export const API = {
  // session & auth
  session: '/api/v1/session', // GET -> SessionInfo (creates a guest session if none)
  authLogin: (returnTo = '/') => `/api/v1/auth/login?returnTo=${enc(returnTo)}`, // GET redirect (Cognito)
  authSignup: (returnTo = '/') => `/api/v1/auth/login?screen=signup&returnTo=${enc(returnTo)}`,
  authGoogle: (returnTo = '/') => `/api/v1/auth/login?provider=google&returnTo=${enc(returnTo)}`,
  authLogout: '/api/v1/auth/logout', // POST -> { redirectTo }
  devLogin: '/api/v1/auth/dev-login', // POST DevLoginBody -> SessionInfo (AUTH_MODE=local only)
  devUsers: '/api/v1/auth/dev-users', // GET -> PublicUser[] (AUTH_MODE=local only)

  // profile
  me: '/api/v1/me', // GET Me | PATCH UpdateProfileBody -> Me | DELETE DeleteAccountBody
  meOwnedRooms: '/api/v1/me/owned-rooms', // GET -> RoomSummary[]
  meFavorites: '/api/v1/me/favorites', // GET -> RoomSummary[]
  meFavorite: (roomId: string) => `/api/v1/me/favorites/${enc(roomId)}`, // PUT | DELETE
  meActivePlaylist: '/api/v1/me/active-playlist', // PUT SetActivePlaylistBody
  avatars: '/api/v1/avatars', // GET -> Avatar[]
  profile: (handle: string) => `/api/v1/users/${enc(handle)}`, // GET -> PublicProfile

  // rooms
  rooms: '/api/v1/rooms', // GET RoomListQuery -> RoomList | POST CreateRoomBody -> RoomDetail
  randomRoom: '/api/v1/rooms/random', // GET -> RandomRoomResult
  room: (slug: string) => `/api/v1/rooms/${enc(slug)}`, // GET RoomDetail | PATCH UpdateRoomBody
  roomArchive: (slug: string) => `/api/v1/rooms/${enc(slug)}/archive`, // POST
  roomHistory: (slug: string) => `/api/v1/rooms/${enc(slug)}/history`, // GET PageQuery -> HistoryPage
  roomChat: (slug: string) => `/api/v1/rooms/${enc(slug)}/chat`, // GET ChatHistoryQuery -> ChatHistory
  roomInvites: (slug: string) => `/api/v1/rooms/${enc(slug)}/invites`, // GET Invite[] | POST CreateInviteBody -> CreatedInvite
  roomInvite: (slug: string, id: string) => `/api/v1/rooms/${enc(slug)}/invites/${enc(id)}`, // DELETE (revoke)
  roomRole: (slug: string, userId: string) => `/api/v1/rooms/${enc(slug)}/roles/${enc(userId)}`, // PUT RoleAssignmentBody
  roomModeration: (slug: string) => `/api/v1/rooms/${enc(slug)}/moderation`, // GET RoomModerationState (moderators)
  acceptInvite: '/api/v1/invites/accept', // POST AcceptInviteBody -> AcceptInviteResult

  // playlists
  playlists: '/api/v1/playlists', // GET PlaylistSummary[] | POST CreatePlaylistBody -> PlaylistDetail
  playlist: (id: string) => `/api/v1/playlists/${enc(id)}`, // GET PlaylistDetail | PATCH rename | DELETE
  playlistItems: (id: string) => `/api/v1/playlists/${enc(id)}/items`, // POST AddPlaylistItemBody -> PlaylistItem
  playlistItem: (id: string, itemId: string) => `/api/v1/playlists/${enc(id)}/items/${enc(itemId)}`, // DELETE
  playlistItemMove: (id: string, itemId: string) =>
    `/api/v1/playlists/${enc(id)}/items/${enc(itemId)}/move`, // POST MovePlaylistItemBody -> PlaylistDetail

  // media
  mediaLookup: '/api/v1/media/lookup', // GET MediaLookupQuery -> Video
  mediaSearch: '/api/v1/media/search', // GET MediaSearchQuery -> MediaSearchResult

  // reports & admin
  reports: '/api/v1/reports', // POST CreateReportBody -> { id }
  adminReports: '/api/v1/admin/reports', // GET ?status=open&cursor -> ReportPage
  adminReport: (id: string) => `/api/v1/admin/reports/${enc(id)}`, // POST ResolveReportBody -> Report
  adminAudit: '/api/v1/admin/audit', // GET ?room=<slug|id>&cursor -> AuditPage

  // ops
  healthLive: '/api/health/live',
  healthReady: '/api/health/ready',
} as const;

export const SOCKET_PATH = '/socket.io';
